import { useFragment } from '@apollo/client';
import {
  Avatar,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Typography,
  useTheme,
} from '@mui/material';
import { useCallback } from 'react';
import { DTOOLS_AUTH_DETAILS_FRAGMENT } from 'src/graphql/entities/integrations/dtools/dtools.fragments';
import {
  DtoolsAuthDetail,
  isDtoolsConnected,
} from 'src/graphql/entities/integrations/dtools/dtools.types';
import { useRevokeDtoolsAuthentication } from 'src/graphql/entities/integrations/dtools/hooks/use-revoke-dtools-authentication';

export type DtoolsIntegrationProps = {
  id: number;
};

export const DtoolsAuthDetails = ({ id }: DtoolsIntegrationProps) => {
  const theme = useTheme();
  const { revokeConnection } = useRevokeDtoolsAuthentication();

  const { data, complete } = useFragment<DtoolsAuthDetail>({
    fragment: DTOOLS_AUTH_DETAILS_FRAGMENT,
    from: {
      __typename: 'DtoolsAuthDetails',
      id,
    },
  });

  const onRevokeHandler = useCallback(async () => {
    await revokeConnection(data.partnerId!);
  }, [data.partnerId, revokeConnection]);

  if (!complete) {
    return null;
  }

  const isInvalid = !isDtoolsConnected(data);

  return (
    <Card variant="outlined">
      <CardHeader
        avatar={<Avatar alt="d-tools-logo" src="/d-tools-logo.png" />}
        title="D-Tools Cloud"
        subheader={isInvalid ? 'Not connected — API key rejected' : 'Connected'}
        slotProps={{
          subheader: {
            color: isInvalid ? theme.palette.error.main : theme.palette.success.main,
          },
        }}
      />
      <CardContent>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          API Key
        </Typography>
        <Typography variant="body1">{data.maskedApiKey}</Typography>
        {isInvalid ? (
          <Typography variant="body2" sx={{ color: 'error.main', mt: theme.spacing() }}>
            D-Tools no longer accepts this API key, so imports will fail. Disconnect and enter
            a new key generated in D-Tools under Settings &gt; Integration &gt; Developer.
          </Typography>
        ) : null}
      </CardContent>
      <CardActions sx={{ m: theme.spacing() }}>
        <Button
          variant="outlined"
          onClick={onRevokeHandler}
          size="small"
          fullWidth
          color="error"
        >
          Disconnect
        </Button>
      </CardActions>
    </Card>
  );
};
